import { StandardEvents, CartLinesUpdateEvent, CartDiscountUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

/**
 * Le cadeau offert par un code, ajouté au panier comme le faisait le site historique.
 *
 * Le legacy portait un champ `cadeau.id_produit` sur ses promotions : le produit
 * apparaissait de lui-même dans le panier. Shopify ne sait pas faire ça : sa réduction
 * « Achetez X, obtenez Y » met Y à zéro **s'il est déjà dans le panier**, elle ne l'y
 * ajoute pas. Pire, saisi sur un panier sans le cadeau, le code est refusé comme
 * inapplicable.
 *
 * Ce script comble l'écart, et rien de plus : il ajoute la ligne, repropose le code,
 * et garde la ligne seulement si Shopify l'a mise à zéro. Il ne décide jamais du
 * prix. C'est ce qui le rend sûr : un client qui trafiquerait le script n'obtiendrait
 * qu'un article au prix fort, et un seuil mal jugé côté navigateur ne laisse jamais un
 * article facturé dans le panier.
 *
 * Trois moments le déclenchent :
 *   · le client saisit le code : le cadeau est posé avant que le code parte (envoi du
 *     formulaire), ou après le refus quand le code arrive autrement (événement de
 *     réduction du thème) ;
 *   · le panier change : un article retiré fait perdre le seuil, le cadeau s'en va ;
 *     le seuil atteint avec le code déjà posé, le cadeau arrive ;
 *   · le panier s'ouvre avec le code déjà posé (lien `/discount/CODE`, visite précédente).
 */

const CONFIG = 'akinod-cadeau-config';
/**
 * Le marqueur de la ligne offerte. C'est aussi lui que lit la passerelle Sylob pour
 * ranger la ligne dans les cadeaux du bon de livraison (`DeliverySections::estCadeau`),
 * et lui qu'`akinod-emballage.js` écarte des articles à emballer.
 */
const MARQUEUR = '_cadeau_offert';
const SOURCE = 'akinod-cadeau';
/** Le client a retiré le cadeau lui-même : on ne le lui remet pas d'office. */
const DECLINE = 'akinod-cadeau-decline';

/** Deux opérations sur le panier ne doivent pas se croiser : on les enchaîne. */
let file = Promise.resolve();

function enfiler(travail) {
  file = file.then(travail, travail);

  return file;
}

/**
 * @returns {{code: string, variante: number, seuil: number, titre: string, verifierAuChargement: boolean} | null}
 */
function configuration() {
  const balise = document.getElementById(CONFIG);

  if (!balise) return null;

  try {
    const lue = JSON.parse(balise.textContent || '{}');

    // Sans code ni variante, il n'y a pas d'offre : on ne fait rien plutôt que
    // d'ajouter un produit au hasard.
    if (!lue.code || !lue.variante) return null;

    return {
      code: String(lue.code).trim(),
      variante: Number(lue.variante),
      seuil: Number(lue.seuil) || 0,
      titre: lue.titre || 'Cadeau offert',
      verifierAuChargement: lue.verifierAuChargement === true,
    };
  } catch {
    return null;
  }
}

const racine = () => window.Shopify?.routes?.root ?? '/';
const memeCode = (a, b) => String(a).toUpperCase() === String(b).toUpperCase();

function lireStockage(cle) {
  try {
    return window.sessionStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrireStockage(cle, valeur) {
  try {
    if (valeur === null) window.sessionStorage.removeItem(cle);
    else window.sessionStorage.setItem(cle, valeur);
  } catch {
    // Sans stockage, un cadeau retiré pourra revenir : c'est gênant, jamais faux.
  }
}

const decline = (offre) => memeCode(lireStockage(DECLINE) ?? '', offre.code);

async function panier() {
  const reponse = await fetch(`${racine()}cart.js`, { headers: { Accept: 'application/json' } });

  return reponse.ok ? reponse.json() : null;
}

const duProduit = (ligne, offre) => Number(ligne.variant_id) === offre.variante;
const marquee = (ligne) => Boolean(ligne.properties?.[MARQUEUR]);

function ligneCadeau(contenu, offre) {
  return (contenu?.items ?? []).find((l) => marquee(l) && duProduit(l, offre));
}

/** Le même produit, choisi par le client : ce n'est pas notre ligne, on n'y touche pas. */
function lignesChoisies(contenu, offre) {
  return (contenu?.items ?? []).filter((l) => !marquee(l) && duProduit(l, offre));
}

/** Shopify a mis la ligne à zéro : elle avait un prix, il ne reste rien à payer. */
function offerte(ligne) {
  return Boolean(ligne) && ligne.original_line_price > 0 && ligne.final_line_price === 0;
}

/**
 * Le montant qui compte pour le seuil : le panier **sans le produit offert**, comme le
 * compte Shopify (un lunch bag à 34,90 € et un totebag ne déclenchent pas l'offre à 35 €).
 * Ce n'est qu'un garde-fou pour ne pas tenter l'ajout en vain : c'est la réduction qui
 * tranche.
 */
function atteintLeSeuil(contenu, offre) {
  const total = (contenu?.items ?? [])
    .filter((l) => !duProduit(l, offre))
    .reduce((somme, l) => somme + l.original_line_price, 0);

  return total >= offre.seuil * 100;
}

function codesDuPanier(contenu) {
  return (contenu?.discount_codes ?? []).map((c) => String(c.code));
}

/**
 * Les sections de panier de la page, et le composant qui porte chacune : c'est le
 * composant à l'origine d'un changement qui redessine dans ce thème, un script
 * extérieur doit donc le faire lui-même (voir `akinod-emballage.js`).
 */
function sectionsDuPanier() {
  return [...document.querySelectorAll('cart-items-component')]
    .filter((n) => n.dataset.sectionId)
    .map((n) => ({ id: n.dataset.sectionId, tiroir: Boolean(n.closest('theme-drawer, cart-drawer-component')) }));
}

/**
 * @param {{rendre?: boolean}} options  `rendre: false` pour une étape intermédiaire,
 *   dont le HTML serait aussitôt périmé.
 */
async function poster(chemin, corps, { rendre = true } = {}) {
  const sections = rendre ? sectionsDuPanier() : [];
  const enrichi = sections.length
    ? { ...corps, sections: sections.map((s) => s.id).join(','), sections_url: window.location.pathname }
    : corps;
  const reponse = await fetch(`${racine()}${chemin}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(enrichi),
  });

  if (!reponse.ok) return null;

  try {
    return { ...(await reponse.json()), ak_sections: sections };
  } catch {
    return null;
  }
}

async function redessiner(resultat) {
  const rendus = resultat?.sections ?? {};

  await Promise.all((resultat?.ak_sections ?? []).map(async ({ id, tiroir }) => {
    if (!rendus[id]) return;

    try {
      await morphSection(id, rendus[id], { mode: tiroir ? 'hydration' : 'full' });
    } catch {
      // Une section absente de la réponse ne doit pas interrompre les autres.
    }
  }));
}

/**
 * Redessine, puis prévient la pastille, le tiroir et l'analytique avec l'événement que
 * le thème attend. Le SDK refuse un événement sans ligne complète.
 */
async function publier(resultat, action, cle, offre) {
  await redessiner(resultat);

  const differe = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(new CartLinesUpdateEvent({
    action,
    context: 'cart',
    lines: [{
      id: cle || String(offre.variante),
      quantity: action === 'remove' ? 0 : 1,
      merchandiseId: `gid://shopify/ProductVariant/${offre.variante}`,
    }],
    promise: differe.promise,
    source: SOURCE,
  }));

  differe.resolve({ detail: { sections: resultat?.sections, itemCount: resultat?.item_count, source: SOURCE } });
}

/**
 * Le message « code non applicable » du thème, que le redessin du panier a masqué. Son
 * message de livraison, qui partage le même cadre, est toujours replié : le thème
 * n'affiche jamais les deux à la fois.
 */
function signalerCodeRefuse(visible = true) {
  for (const bloc of document.querySelectorAll('cart-discount-component')) {
    bloc.querySelector('[ref="cartDiscountError"]')?.classList.toggle('hidden', !visible);
    bloc.querySelector('[ref="cartDiscountErrorDiscountCode"]')?.classList.toggle('hidden', !visible);
    bloc.querySelector('[ref="cartDiscountErrorShipping"]')?.classList.add('hidden');
  }
}

/**
 * Le bouton « Appliquer » pendant qu'on pose le cadeau : une roue à la place du texte
 * (styles dans `akinod-cadeau.liquid`), et désactivé, ce qui coupe aussi la touche
 * Entrée. Sans lui, le champ restait figé une à deux secondes sans rien dire.
 */
function attendre(actif) {
  for (const bouton of document.querySelectorAll('cart-discount-component .cart-discount__button')) {
    bouton.disabled = actif;

    if (actif) bouton.setAttribute('aria-busy', 'true');
    else bouton.removeAttribute('aria-busy');
  }
}

/** Les codes acceptés, comme le thème les relit avant d'en proposer un nouveau. */
function pastilles(bloc) {
  return [...bloc.querySelectorAll('.cart-discount__pill')]
    .map((pastille) => pastille.dataset.discountCode)
    .filter(Boolean);
}

/**
 * Comme dans le parcours natif, une saisie refusée ne reste pas au panier : la
 * reproposer avec le cadeau l'y a accrochée.
 */
async function retirerLeCodeRefuse(offre) {
  const gardes = codesDuPanier(await panier()).filter((c) => !memeCode(c, offre.code));

  await redessiner(await poster('cart/update.js', { discount: gardes.join(',') }));
  signalerCodeRefuse();
}

/**
 * Pose le cadeau, repropose les codes, et ne garde la ligne que si Shopify l'offre.
 *
 * @param {string[]} codes  les codes à reproposer, dont celui de l'offre
 * @returns {Promise<boolean>} vrai si le cadeau est offert
 */
async function tenterLeCadeau(offre, codes) {
  const ajout = await poster('cart/add.js', {
    items: [{ id: offre.variante, quantity: 1, properties: { [MARQUEUR]: offre.titre } }],
  }, { rendre: false });

  if (!ajout) return false;

  // Un code refusé n'offre rien tant qu'on ne le repropose pas avec le cadeau au
  // panier. `cart/update.js` renvoie le panier entier, et les sections.
  const reponse = await poster('cart/update.js', { discount: codes.join(',') });

  /* Sans réponse, on ignore ce que la réduction a fait de la ligne qu'on vient de
     poser : on relit le panier. La juger sur la réponse absente laissait le cadeau
     au panier, au prix fort, sans que le client l'ait choisi ni même qu'il le voie,
     puisque l'ajout n'avait rien redessiné. Si la relecture échoue aussi, le
     prochain `ajuster()` retire la ligne : elle est marquée, et pas offerte. */
  const etat = reponse ?? await panier();
  const ligne = ligneCadeau(etat, offre);
  const accepte = (etat?.discount_codes ?? []).some((c) => c.applicable === true && memeCode(c.code, offre.code));

  if (accepte && offerte(ligne)) {
    // Relu sans sections : un aller-retour neutre sur la ligne offerte les rapporte.
    await publier(reponse ?? await poster('cart/change.js', { id: ligne.key, quantity: ligne.quantity }), 'add', ligne.key, offre);

    return true;
  }

  // Le code n'offre pas le cadeau à ce panier : on retire ce qu'on a posé, et le panier
  // redevient celui que Shopify aurait laissé.
  if (ligne) {
    await publier(await poster('cart/change.js', { id: ligne.key, quantity: 0 }), 'remove', ligne.key, offre);
  }

  return false;
}

/**
 * Remet le panier d'aplomb : le cadeau présent seulement s'il est offert, et posé dès
 * qu'il peut l'être avec le code déjà au panier.
 */
function ajuster() {
  const offre = configuration();

  if (!offre) return Promise.resolve();

  return enfiler(async () => {
    try {
      const contenu = await panier();

      if (!contenu) return;

      const ligne = ligneCadeau(contenu, offre);

      if (ligne) {
        if (offerte(ligne)) return;

        // Le cadeau n'est plus offert : code retiré, seuil perdu, ou gratuité portée par
        // un totebag que le client a ajouté lui-même. Dans tous les cas on ne laisse pas
        // au client un article facturé qu'il n'a pas choisi : il garde le sien, offert.
        await publier(await poster('cart/change.js', { id: ligne.key, quantity: 0 }), 'remove', ligne.key, offre);

        return;
      }

      // Le produit est déjà là, choisi par le client : la réduction le lui offre sans
      // qu'on en ajoute un second.
      if (lignesChoisies(contenu, offre).length > 0 || decline(offre)) return;

      const codes = codesDuPanier(contenu);

      if (codes.some((c) => memeCode(c, offre.code)) && atteintLeSeuil(contenu, offre)) {
        await tenterLeCadeau(offre, codes);
      }
    } catch {
      // Un cadeau qui n'arrive pas ne doit jamais empêcher d'acheter.
    }
  });
}

/** Vrai le temps de rendre une saisie au thème : l'écoute de l'envoi la laisse passer. */
let laisserAuTheme = false;

/**
 * Renvoie le formulaire tel que le client l'a validé, cette fois jusqu'au thème. Le
 * thème émet son événement de réduction pendant cet envoi, sans attendre : l'écouteur
 * plus bas remet la roue avant que le navigateur ait pu peindre le bouton libre.
 */
function rendreAuTheme(formulaire) {
  attendre(false);
  laisserAuTheme = true;

  try {
    formulaire.requestSubmit();
  } finally {
    laisserAuTheme = false;
  }
}

/**
 * Pose le cadeau, puis envoie le code une seule fois.
 *
 * Le panier est relu d'abord : sans cadeau à poser (panier vide, sous le seuil, produit
 * déjà là), la saisie repart au thème, dont la réponse est alors la bonne, refus compris.
 */
async function appliquerAvecLeCadeau(formulaire, bloc, champ, offre) {
  const contenu = await panier().catch(() => null);

  if (!contenu || contenu.item_count === 0
    || ligneCadeau(contenu, offre) || lignesChoisies(contenu, offre).length > 0
    || !atteintLeSeuil(contenu, offre)) {
    rendreAuTheme(formulaire);

    return;
  }

  const codes = [...pastilles(bloc).filter((c) => !memeCode(c, offre.code)), champ.value.trim()];

  // L'événement que le thème aurait émis : la pop-up, l'emballage et l'analytique
  // l'écoutent. Marqué de notre source, pour que l'écouteur plus bas l'ignore.
  const differe = CartDiscountUpdateEvent.createPromise();

  bloc.dispatchEvent(new CartDiscountUpdateEvent({
    discountCodes: codes.map((code) => ({ code })),
    promise: differe.promise,
    source: SOURCE,
  }));

  try {
    if (!(await tenterLeCadeau(offre, codes))) await retirerLeCodeRefuse(offre);
  } catch {
    // Jamais bloquant : la ligne posée, si elle n'est pas offerte, part au prochain
    // `ajuster()`, et le code n'est pas affiché comme accepté.
    signalerCodeRefuse();
    ajuster();
  }

  // Le thème vide le champ dans les deux cas, accepté ou refusé.
  champ.value = '';
  attendre(false);

  const etat = await panier().catch(() => null);

  if (etat) differe.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(etat) });
  else differe.reject(new Error('Panier illisible après le code cadeau'));
}

/*
 * Le client valide le code de l'offre dans le panier. Parti tel quel, Shopify le
 * refusait faute de cadeau au panier, et on le reproposait une fois le cadeau posé :
 * un aller-retour perdu, le plus long (1,2 s mesurées le 15/10, sur 2,7 s jusqu'au
 * totebag). On pose le cadeau d'abord, et le code ne part qu'une fois.
 *
 * Écouté sur `window` en capture : le thème écoute les envois sur `document`, en
 * capture lui aussi, et `window` passe avant lui.
 */
window.addEventListener('submit', (evenement) => {
  if (laisserAuTheme) return;

  const formulaire = evenement.target;
  const bloc = formulaire instanceof HTMLFormElement ? formulaire.closest('cart-discount-component') : null;
  const champ = bloc?.querySelector('input[name="discount"]');
  const offre = configuration();

  if (!offre || !(champ instanceof HTMLInputElement) || !memeCode(champ.value.trim(), offre.code)) return;

  evenement.preventDefault();
  evenement.stopImmediatePropagation();

  // Déjà accepté : on ne fait rien, comme le thème devant un doublon exact. Lui compare
  // à la casse près : « thinkpink » partait en doublon, et Shopify le disait « non
  // applicable » sur un panier où le code l'était.
  if (pastilles(bloc).some((c) => memeCode(c, offre.code))) return;

  // Saisir le code, c'est redemander le cadeau.
  ecrireStockage(DECLINE, null);
  signalerCodeRefuse(false);
  attendre(true);

  enfiler(() => appliquerAvecLeCadeau(formulaire, bloc, champ, offre).catch(() => attendre(false)));
}, { capture: true });

// Un code arrivé par le thème sans passer par l'envoi ci-dessus : rendu par lui, ou
// envoyé avant que ce script ne se charge. On attend la réponse de Shopify : s'il a
// refusé le code de l'offre faute de cadeau au panier, on le pose et on repropose.
document.addEventListener(StandardEvents.cartDiscountUpdate, async (evenement) => {
  if (evenement.source === SOURCE) return;

  const offre = configuration();

  if (!offre) return;

  const proposes = (evenement.discountCodes ?? []).map((c) => String(c.code));

  if (!proposes.some((c) => memeCode(c, offre.code))) return;

  // Saisir le code, c'est redemander le cadeau.
  ecrireStockage(DECLINE, null);
  attendre(true);

  let resultat = null;

  try {
    resultat = await evenement.promise;
  } catch {
    attendre(false);

    return;
  }

  const refuse = (resultat?.cart?.discountCodes ?? []).some((c) => memeCode(c.code, offre.code) && c.applicable === false);

  if (!refuse) {
    // Accepté d'emblée : le produit est déjà au panier. Rien à ajouter, mais une ligne
    // offerte a pu devenir inutile.
    ajuster().finally(() => attendre(false));

    return;
  }

  /* Le thème vient d'afficher « code non applicable » : c'est le refus de Shopify sans
     le cadeau au panier, que la suite lève le plus souvent. Affiché, le message restait
     sous les yeux du client jusqu'à l'arrivée du totebag, 1,5 s plus tard. On l'efface
     ici, avant tout `await` : le thème l'a montré dans la même tâche, le navigateur ne
     le peint donc pas. Il ne revient que si le refus tient. */
  signalerCodeRefuse(false);

  enfiler(async () => {
    try {
      const contenu = await panier();

      // Sous le seuil, ou avec le produit déjà choisi, le refus de Shopify est la bonne
      // réponse : on le remontre tel quel.
      if (!contenu || contenu.item_count === 0
        || ligneCadeau(contenu, offre) || lignesChoisies(contenu, offre).length > 0
        || !atteintLeSeuil(contenu, offre)) {
        signalerCodeRefuse();

        return;
      }

      const codes = [...new Set([...codesDuPanier(contenu), ...proposes])];

      if (await tenterLeCadeau(offre, codes)) return;

      await retirerLeCodeRefuse(offre);
    } catch {
      // Jamais bloquant, et jamais muet : le refus du thème reste affiché.
      signalerCodeRefuse();
    } finally {
      attendre(false);
    }
  });
});

// Le panier a changé sans nous. Le thème émet l'événement avant son aller-retour serveur :
// on attend la promesse, sinon on lirait le panier d'avant.
document.addEventListener(StandardEvents.cartLinesUpdate, async (evenement) => {
  if ((evenement.source ?? evenement.detail?.source) === SOURCE) return;

  const offre = configuration();

  if (!offre) return;

  // Le client retire lui-même la ligne offerte : c'est un choix, on le respecte jusqu'à
  // ce qu'il saisisse le code à nouveau.
  const retraits = (evenement.lines ?? []).filter((l) => Number(l.quantity) === 0).map((l) => String(l.id));
  const cadeauAffiche = retraits.some((cle) => document.querySelector(`.ak-ligne-cadeau[data-key="${CSS.escape(cle)}"]`));

  try {
    await evenement.promise;
  } catch {
    return;
  }

  if (cadeauAffiche) ecrireStockage(DECLINE, offre.code);

  ajuster();
});

if (configuration()?.verifierAuChargement) ajuster();
